/**
 * Settings → Plan. A plan belongs to the business, not to a person: where it stands (trial, paid,
 * read-only), the three plans side by side, monthly or yearly. Only the owner subscribes or changes the plan; everyone else sees what
 * the business has. Limits come from `lib/plan/plan.ts`, prices from services/billing.
 */
import { Check, CreditCard } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  useCheckout,
  useInvalidateSubscription,
  usePlans,
  usePortal,
  useWorkspaceSubscription,
} from "@/hooks/use-billing";
import { track } from "@/lib/platform/analytics";
import type { Plan } from "@/lib/api/billing";
import { redirect } from "@/lib/platform/browser";
import { formatDate, formatMoney } from "@/lib/utils/format";
import { GRACE_DAYS, LIMITS, type Interval, type PlanId } from "@/lib/plan/plan";
import { cn } from "@/lib/utils/cn";
import { useWorkspace } from "@/lib/session/workspace-context";

/** The last day a past-due plan keeps working (7 days from the first failed payment). */
const graceEnd = (pastDueSince: string) =>
  new Date(new Date(pastDueSince.replace(" ", "T")).getTime() + GRACE_DAYS * 86400000);

export function PlanSettings() {
  const { t } = useTranslation();
  const { workspace, role, plan: state } = useWorkspace();
  const { data: subscription } = useWorkspaceSubscription(workspace?.id);
  const { data: plans } = usePlans();
  const checkout = useCheckout();
  const portal = usePortal();
  const invalidate = useInvalidateSubscription();
  const [params, setParams] = useSearchParams();
  const [interval, setBilling] = useState<Interval>(
    subscription?.interval === "year" ? "year" : "month",
  );
  const isOwner = role === "owner";
  const paying = state?.kind === "paid";

  // Back from Checkout: `?checkout=success|cancelled`. The ref keeps StrictMode's double effect
  // from toasting twice.
  const outcome = params.get("checkout");
  const handled = useRef<string | null>(null);
  useEffect(() => {
    if (!outcome || handled.current === outcome) return;
    handled.current = outcome;
    if (outcome === "success") {
      toast.success(t("plans.checkoutSuccess"));
      track("checkout_completed");
      void invalidate();
    } else {
      toast.info(t("plans.checkoutCancelled"));
    }
    setParams({ tab: "plan" }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outcome]);

  async function choose(plan: PlanId) {
    if (!workspace) return;
    try {
      track("checkout_started", { plan, interval });
      redirect(await checkout.mutateAsync({ workspace: workspace.id, plan, interval }));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("plans.checkoutError"));
    }
  }

  async function manage() {
    if (!workspace) return;
    try {
      redirect(await portal.mutateAsync(workspace.id));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("plans.portalError"));
    }
  }

  return (
    <section className="space-y-8">
      <p className="text-sm text-muted-foreground">
        {t("plans.subtitle", { name: workspace?.name ?? "" })}
      </p>

      {!state ? (
        <Skeleton className="h-32 w-full" aria-busy="true" />
      ) : (
        <Card data-testid="current-plan">
          <CardHeader>
            <CardDescription>{t("plans.current")}</CardDescription>
            <CardTitle className="flex flex-wrap items-center gap-2">
              <span data-testid="current-plan-name">
                {state.kind === "paid"
                  ? t(`plans.names.${state.plan}`)
                  : state.kind === "trial"
                    ? t("plans.trial")
                    : t("plans.readOnly")}
              </span>
              {state.kind === "paid" && subscription?.interval && (
                <Badge variant="secondary">{t(`plans.intervals.${subscription.interval}`)}</Badge>
              )}
              {state.kind === "paid" && state.status === "past_due" && (
                <Badge variant="destructive" data-testid="plan-past-due">
                  {t("plans.pastDue")}
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent
            className="space-y-1 text-sm text-muted-foreground"
            data-testid="current-plan-text"
          >
            {state.kind === "trial" && (
              <p>
                {t("plans.trialText", { count: state.daysLeft, date: formatDate(state.endsAt) })}
              </p>
            )}
            {state.kind === "expired" && <p>{t("plans.readOnlyText")}</p>}
            {state.kind === "paid" && state.status === "past_due" && subscription?.pastDueSince && (
              <p className="text-foreground" data-testid="grace-until">
                {t("plans.graceUntil", { date: formatDate(graceEnd(subscription.pastDueSince)) })}
              </p>
            )}
            {state.kind === "paid" && subscription?.currentPeriodEnd && (
              <p>
                {subscription.cancelAtPeriodEnd
                  ? t("plans.endsOn", { date: formatDate(subscription.currentPeriodEnd) })
                  : t("plans.renews", { date: formatDate(subscription.currentPeriodEnd) })}
              </p>
            )}
            {state.kind === "paid" && Boolean(subscription?.unitAmount) && (
              <p data-testid="price-lock">
                {t(`plans.priceLock.${subscription?.interval === "year" ? "year" : "month"}`, {
                  amount: formatMoney((subscription?.unitAmount ?? 0) / 100),
                })}
              </p>
            )}
          </CardContent>
          {isOwner && paying && subscription?.stripeCustomerId && (
            <CardFooter>
              <Button
                variant="outline"
                onClick={() => void manage()}
                disabled={portal.isPending}
                data-testid="manage-subscription"
              >
                <CreditCard /> {t("plans.manage")}
              </Button>
            </CardFooter>
          )}
        </Card>
      )}

      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Tabs value={interval} onValueChange={(value) => setBilling(value as Interval)}>
            <TabsList>
              <TabsTrigger value="month" data-testid="interval-month">
                {t("plans.monthly")}
              </TabsTrigger>
              <TabsTrigger value="year" data-testid="interval-year">
                {t("plans.yearly")}
              </TabsTrigger>
            </TabsList>
          </Tabs>
          {!isOwner && (
            <p className="text-sm text-muted-foreground" data-testid="owner-only">
              {t("plans.ownerOnly")}
            </p>
          )}
        </div>

        <div className="grid gap-4 lg:grid-cols-3" data-testid="plan-list">
          {plans?.map((plan) => (
            <PlanCard
              key={plan.id}
              plan={plan}
              interval={interval}
              current={paying && state.plan === plan.id}
              action={
                !isOwner
                  ? null
                  : paying
                    ? state.plan === plan.id
                      ? null
                      : () => void manage()
                    : () => void choose(plan.id)
              }
              busy={checkout.isPending || portal.isPending}
              changeInPortal={paying}
            />
          ))}
        </div>
        <p className="text-sm text-muted-foreground">{t("plans.footnote")}</p>
      </div>
    </section>
  );
}

function PlanCard({
  plan,
  interval,
  current,
  action,
  busy,
  changeInPortal,
}: {
  plan: Plan;
  interval: Interval;
  current: boolean;
  /** Null: nothing to do here (the current plan, or not the owner). */
  action: (() => void) | null;
  busy: boolean;
  changeInPortal: boolean;
}) {
  const { t } = useTranslation();
  const price = plan.prices[interval];
  const limits = LIMITS[plan.id];
  const perMonth = interval === "year" ? price.amount / 12 : price.amount;
  const unlimited = t("plans.features.unlimited");
  const features = [
    t("plans.features.receipts", {
      value: limits.receiptsPerMonth === null ? unlimited : limits.receiptsPerMonth,
    }),
    t("plans.features.accounts", { value: limits.accounts ?? unlimited }),
    t("plans.features.users", { value: limits.users ?? unlimited }),
    limits.accountingExport ? t("plans.features.accountingExport") : t("plans.features.zipOnly"),
    ...(plan.id === "plus" ? [t("plans.features.businesses")] : []),
  ];

  return (
    <Card
      className={cn("flex flex-col", plan.id === "standard" && "border-primary/50")}
      data-testid={`plan-${plan.id}`}
    >
      <CardHeader>
        <CardTitle className="flex items-center justify-between gap-2">
          {t(`plans.names.${plan.id}`)}
          {plan.id === "standard" && <Badge>{t("plans.recommended")}</Badge>}
        </CardTitle>
        <CardDescription>{t(`plans.descriptions.${plan.id}`)}</CardDescription>
      </CardHeader>
      <CardContent className="flex-1 space-y-4">
        <div>
          <p className="text-3xl font-semibold tracking-tight num" data-testid="plan-price">
            {formatMoney(perMonth / 100)}
            <span className="text-sm font-normal text-muted-foreground">
              {" "}
              {t("plans.perMonth")}
            </span>
          </p>
          <p className="text-xs text-muted-foreground">
            {interval === "year"
              ? t("plans.billedYearly", { amount: formatMoney(price.amount / 100) })
              : t("plans.billedMonthly")}
          </p>
        </div>
        <ul className="space-y-2 text-sm">
          {features.map((feature) => (
            <li key={feature} className="flex gap-2">
              <Check className="mt-0.5 size-4 shrink-0 text-primary" />
              {feature}
            </li>
          ))}
        </ul>
      </CardContent>
      <CardFooter>
        <Button
          className="w-full"
          variant={plan.id === "standard" ? "default" : "outline"}
          disabled={!action || !price.configured || busy}
          onClick={action ?? undefined}
          data-testid={`choose-${plan.id}`}
        >
          {current
            ? t("plans.active")
            : !price.configured
              ? t("plans.notConfigured")
              : changeInPortal
                ? t("plans.change", { plan: t(`plans.names.${plan.id}`) })
                : t("plans.choose", { plan: t(`plans.names.${plan.id}`) })}
        </Button>
      </CardFooter>
    </Card>
  );
}
